import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { DEVICE_MANAGEMENT_LINKS_URL } from '../../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import { mapSortItemParam } from './device-management-link-sort.mapper'
import filterMapper from './device-management-link-filter.mapper'
import {
  DeviceManagementLinkDto,
  DeviceManagementLinkDraftPoint,
} from '../../../dto/device/device-management/device-management-links/DeviceManagementLink.dto'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<DeviceManagementLinkDto>> => {
    const filterParams = `c=>${filterParamsComposer('c', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortItemParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<DeviceManagementLinkDto[]>(
      `${DEVICE_MANAGEMENT_LINKS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<DeviceManagementLinkDto>> => {
    const data = await get<DeviceManagementLinkDto>(
      DEVICE_MANAGEMENT_LINKS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<DeviceManagementLinkDto>> => {
    const data = await get<DeviceManagementLinkDto[]>(
      `${DEVICE_MANAGEMENT_LINKS_URL}/GetByConditions`,
      `/u=>new int[] {${ids.toString()}}.Contains(u.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  update: async (
    resource: string,
    { data }: UpdateParams<DeviceManagementLinkDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      DeviceManagementLinkDto
    >(DEVICE_MANAGEMENT_LINKS_URL, {
      ...data,
    })
    return Promise.resolve({ data: update })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateDeviceManagementLinkRequest>,
  ): Promise<CreateResult<DeviceManagementLinkDto>> => {
    const deviceManagementLink = await post<
      CreateDeviceManagementLinkRequest[],
      DeviceManagementLinkDto[]
    >(DEVICE_MANAGEMENT_LINKS_URL, [data])
    return {
      data: deviceManagementLink[0],
    }
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult<DeviceManagementLinkDto>> => {
    const deleted = await remove<DeviceManagementLinkDto>(
      `${DEVICE_MANAGEMENT_LINKS_URL}/`,
      id as string,
    )
    return {
      data: deleted,
    }
  },
} as DataProvider

export interface CreateDeviceManagementLinkRequest {
  readonly id: string
  readonly containerId: string
  readonly targetContainerId: string
  readonly coordinates: DeviceManagementLinkDraftPoint[]
}

export default provider
